/**
 * Toast Notification Component
 * Displays toast notifications using DaisyUI alert styles.
 * Positioned at the bottom-right corner of the screen.
 */

import {
  Component,
  inject,
  ChangeDetectionStrategy,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ToastService,
  Toast,
  ToastType,
} from '../../../core/services/toast.service';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(100%)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'translateX(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in',
          style({ opacity: 0, transform: 'translateX(100%)' }),
        ),
      ]),
    ]),
  ],
  template: `
    <div
      class="app-toast-container toast toast-end toast-bottom z-[9999] mb-4 mr-2 sm:mr-4"
    >
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          [@toastAnimation]
          class="app-toast-alert alert shadow-lg max-w-sm"
          [ngClass]="getAlertClass(toast.type)"
          role="alert"
        >
          <!-- Icon -->
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            [attr.stroke]="'currentColor'"
          >
            @switch (toast.type) {
              @case ('success') {
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              }
              @case ('error') {
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              }
              @case ('warning') {
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              }
              @default {
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              }
            }
          </svg>

          <!-- Content -->
          <div class="flex flex-col min-w-0 flex-1 overflow-hidden">
            @if (toast.title) {
              <span class="font-bold text-sm truncate">{{ toast.title }}</span>
            }
            <span class="text-sm line-clamp-2 break-words">{{
              toast.message
            }}</span>
          </div>

          <!-- Dismiss button -->
          @if (toast.dismissible) {
            <button
              class="btn btn-ghost btn-sm btn-circle shrink-0"
              (click)="dismiss(toast.id)"
              aria-label="Dismiss notification"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      /* Toast container - scoped styles */
      app-toast {
        position: fixed;
        /* Position above bottom navigation bar using CSS variable anchor */
        bottom: var(--bottom-nav-height, 0px);
        right: 0;
        z-index: 9999;
        pointer-events: none;
      }

      /* On desktop (no bottom nav), use standard spacing */
      @media (min-width: 768px) {
        app-toast {
          bottom: 0;
        }
      }

      .app-toast-container {
        pointer-events: auto;
        position: fixed !important;
        /* Position above bottom navigation bar using CSS variable anchor */
        bottom: calc(var(--bottom-nav-height, 0px) + 1rem) !important;
        right: 0 !important;
        z-index: 9999 !important;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding-right: 0.25rem;
      }

      /* On larger screens, use more spacing */
      @media (min-width: 640px) {
        .app-toast-container {
          padding-right: 0.5rem;
        }
      }

      /* On desktop (no bottom nav), use standard spacing */
      @media (min-width: 768px) {
        .app-toast-container {
          bottom: 1rem !important;
        }
      }

      .app-toast-alert {
        min-width: 280px;
        max-width: calc(100vw - 1rem);
        display: flex !important;
        align-items: flex-start !important;
        gap: 0.75rem;
        padding: 1rem !important;
        border-radius: 0.5rem !important;
        box-shadow:
          0 10px 15px -3px rgba(0, 0, 0, 0.1),
          0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
      }

      /* Cap max-width on larger screens */
      @media (min-width: 640px) {
        .app-toast-alert {
          max-width: 350px;
        }
      }

      /* Error toast styling */
      .app-toast-alert.alert-error {
        background-color: #fef2f2 !important;
        border: 1px solid #fecaca !important;
        color: #991b1b !important;
      }

      .app-toast-alert.alert-error svg {
        color: #dc2626 !important;
      }

      /* Success toast styling */
      .app-toast-alert.alert-success {
        background-color: #f0fdf4 !important;
        border: 1px solid #bbf7d0 !important;
        color: #166534 !important;
      }

      .app-toast-alert.alert-success svg {
        color: #16a34a !important;
      }

      /* Warning toast styling */
      .app-toast-alert.alert-warning {
        background-color: #fffbeb !important;
        border: 1px solid #fde68a !important;
        color: #92400e !important;
      }

      .app-toast-alert.alert-warning svg {
        color: #d97706 !important;
      }

      /* Info toast styling */
      .app-toast-alert.alert-info {
        background-color: #eff6ff !important;
        border: 1px solid #bfdbfe !important;
        color: #1e40af !important;
      }

      .app-toast-alert.alert-info svg {
        color: #2563eb !important;
      }

      /* Dismiss button styling */
      .app-toast-alert .btn-ghost {
        background: transparent !important;
        border: none !important;
        padding: 0.25rem !important;
        min-height: auto !important;
        height: auto !important;
        opacity: 0.6;
        transition: opacity 0.2s;
      }

      .app-toast-alert .btn-ghost:hover {
        background: rgba(0, 0, 0, 0.1) !important;
        opacity: 1;
      }

      /* Content area */
      .app-toast-alert .flex-col {
        flex: 1;
        min-width: 0;
      }

      /* Limit message lines */
      .app-toast-alert .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    `,
  ],
})
export class ToastComponent {
  protected toastService = inject(ToastService);

  getAlertClass(type: ToastType): string {
    switch (type) {
      case 'success':
        return 'alert-success';
      case 'error':
        return 'alert-error';
      case 'warning':
        return 'alert-warning';
      case 'info':
      default:
        return 'alert-info';
    }
  }

  dismiss(id: string): void {
    this.toastService.dismiss(id);
  }
}
